import {
    memo,
    type SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { BookOutlined, FileTextOutlined } from '@ant-design/icons';
import { Calendar, DatePicker, Flex, Typography } from 'antd';
import cn from 'classnames';
import dayjs from 'dayjs';

import { Card } from '@/shared/ui/card';
import { Loader } from '@/shared/ui/loader';

import { useLazyGetAccountCalendarDataQuery } from '../../api/trading-platform.api';
import { calendarDataTransformer } from '../../lib/calendar-data-transformer';
import { parseNegativeNumber } from '../../lib/parse-negative-number';
import { useGetTradingPlatformSessionCurrency } from '../../models/selectors/get-trading-platform-session-currency';
import { useGetTradingPlatformSessionId } from '../../models/selectors/get-trading-platform-session-id';
import { useGetTradingPlatformSessionType } from '../../models/selectors/get-trading-platform-session-type';
import type { CalendarData } from '../../models/types/transformed-calendar-data';
import { TradingPlatformDailyInfo } from '../trading-platform-daily-info/trading-platform-daily-info';
import { TradingPlatformMonthSwitcher } from '../trading-platform-month-switcher/trading-platform-month-switcher';
import type { TradingCalendarProps } from '../trading-platform-monthly-trades-card/trading-platform-monthly-trades-card';

import cls from './trading-platform-calendar.module.css';

interface CustomCalendarCellProps {
    data: CalendarData;
    onOpen: (data: CalendarData) => void;
}

const CustomCalendarCell = memo((props: CustomCalendarCellProps) => {
    const {
        data: { date, total_profit: totalProfit, trades, has_note: hasNote },
        onOpen,
    } = props;

    const { t } = useTranslation('trading-journal');

    const currency = useGetTradingPlatformSessionCurrency();
    const currentDate = dayjs(date);
    const isToday = currentDate.startOf('day').isSame(dayjs().startOf('day'));
    const isLossDay = totalProfit < 0;
    const isWinDay = totalProfit >= 0;

    const onClick = useCallback(
        (event: SyntheticEvent) => {
            event.stopPropagation();

            onOpen(props.data);
        },
        [onOpen, props.data],
    );

    return (
        <div
            className={cn(
                'algotech-picker-cell-inner algotech-picker-calendar-date',
                cls.customCell,
                {
                    'algotech-picker-calendar-date-today': isToday,
                    [cls.winDay]: isWinDay,
                    [cls.lossDay]: isLossDay,
                },
            )}
            onClick={onClick}
        >
            <div
                className={cn(
                    'algotech-picker-calendar-date-value',
                    cls.dateValueContainer,
                    cls.dateValue,
                )}
            >
                <div className={cn(cls.iconContainer)}>
                    <BookOutlined />
                    {hasNote && (
                        <FileTextOutlined className={cls.noteIndicator} />
                    )}
                </div>
                {currentDate.format('DD')}
            </div>
            <div className="algotech-picker-calendar-date-content">
                <Flex
                    align="end"
                    className="full-height"
                    justify="end"
                    vertical
                >
                    <Typography.Text className={cls.value}>
                        {parseNegativeNumber(totalProfit, currency)}
                    </Typography.Text>
                    <Typography.Text className={cls.tradesValue}>
                        {t('tradingJournalTradesCount', {
                            count: trades?.length,
                        })}
                    </Typography.Text>
                </Flex>
            </div>
        </div>
    );
});

CustomCalendarCell.displayName = 'CustomCalendarCell';

interface DefaultCalendarCellProps {
    date: dayjs.Dayjs;
}

const DefaultCalendarCell = memo((props: DefaultCalendarCellProps) => {
    const { date } = props;

    const isToday = date.startOf('day').isSame(dayjs().startOf('day'));

    return (
        <div
            className={cn(
                'algotech-picker-cell-inner algotech-picker-calendar-date',
                {
                    'algotech-picker-calendar-date-today': isToday,
                },
            )}
        >
            <div className="algotech-picker-calendar-date-value">
                {date.format('DD')}
            </div>
            <div className="algotech-picker-calendar-date-content" />
        </div>
    );
});

DefaultCalendarCell.displayName = 'DefaultCalendarCell';

export const TradingPlatformCalendar = memo((props: TradingCalendarProps) => {
    const { value, setValue } = props;
    const modalData = useRef<CalendarData>();

    const accountId = useGetTradingPlatformSessionId();
    const connectionType = useGetTradingPlatformSessionType();

    const [
        getAccountCalendarDataQuery,
        { data: calendarData, isFetching: isGetAccountCalendarDataFetching },
    ] = useLazyGetAccountCalendarDataQuery();

    const [isOpened, setIsOpened] = useState(false);

    const onOpen = useCallback((data: CalendarData) => {
        setIsOpened(true);

        modalData.current = data;
    }, []);

    const onClose = useCallback(() => {
        setIsOpened(false);
    }, []);

    const transformedCalendarData = useMemo(
        () => calendarDataTransformer(calendarData),
        [calendarData],
    );

    const cellRenderCb = useCallback(
        (date: dayjs.Dayjs) => {
            const data = transformedCalendarData.get(date.format('YYYY-MM-DD'));

            return data ? (
                <CustomCalendarCell
                    data={data}
                    onOpen={onOpen}
                />
            ) : (
                <DefaultCalendarCell date={date} />
            );
        },
        [onOpen, transformedCalendarData],
    );

    const onChangeDatePicker = useCallback(
        (newValue: dayjs.Dayjs) => {
            setValue(newValue);
        },
        [setValue],
    );

    const onPrevMonthHandler = useCallback(() => {
        setValue((prevValue) => prevValue.subtract(1, 'month'));
    }, [setValue]);

    const onNextMonthHandler = useCallback(() => {
        setValue((prevValue) => prevValue.add(1, 'month'));
    }, [setValue]);

    const validRange = useMemo<[dayjs.Dayjs, dayjs.Dayjs]>(
        () => [value.startOf('month'), value.endOf('month')],
        [value],
    );

    useEffect(() => {
        if (accountId && connectionType) {
            getAccountCalendarDataQuery({
                id: accountId,
                date: value.startOf('month').format('YYYY-MM-DD'),
                connectionType,
            });
        }
    }, [accountId, getAccountCalendarDataQuery, value, connectionType]);

    return (
        <>
            <Card
                className={cls.root}
                headerActions={
                    <Card
                        hasShadow={false}
                        isWithoutPadding
                    >
                        <DatePicker
                            allowClear={false}
                            disabled={isGetAccountCalendarDataFetching}
                            onChange={onChangeDatePicker}
                            picker="month"
                            readOnly
                            value={value}
                        />
                    </Card>
                }
                isCollapsible
                isWithoutPadding
                title={
                    <TradingPlatformMonthSwitcher
                        isLoading={isGetAccountCalendarDataFetching}
                        onNextMonth={onNextMonthHandler}
                        onPrevMonth={onPrevMonthHandler}
                        value={value}
                    />
                }
            >
                <Calendar
                    fullCellRender={cellRenderCb}
                    headerRender={() => null}
                    validRange={validRange}
                    value={value}
                />
                {isGetAccountCalendarDataFetching && <Loader />}
            </Card>

            <TradingPlatformDailyInfo
                data={modalData.current}
                isOpened={isOpened}
                onClose={onClose}
            />
        </>
    );
});

TradingPlatformCalendar.displayName = 'TradingPlatformCalendar';
